import React, { FC } from "react";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "usehooks-ts";

import { getInitialsFromFullName } from "../../constants/constants";
import { navigateToContact } from "../../features/contacts/utils/navigateToContacts";
import { useAppSelector } from "../../redux/redux";
import { getLinkedFirm } from "../../sessionStorage/sessionStorage";
import { useGetSearchCustomersQuery } from "../../slices/ContactsSlice";
import { SelectOptionType } from "../../types/types";
import CustomSelect from "../select/Select";

type Props = {
  setValue: (data: string) => void;
  value: string;
  placeholder: string;
  close?: () => void;
  type: "contact" | "organization";
  small?: boolean;
  removeSelectIcon?: boolean;
};

const SearchContacts: FC<Props> = ({
  setValue,
  value,
  placeholder,
  close,
  type,
  small,
  removeSelectIcon,
}) => {
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.appReducer);

  const debounceSearch = useDebounce(value, 300);

  const { data: contacts, isFetching: isLoadingContacts } =
    useGetSearchCustomersQuery({
      searchQuery: debounceSearch || "ab",
      orgId: getLinkedFirm()?.orgId || "",
      userId: user.id,
      isActive: true,
    });

  const getOptions = (): SelectOptionType[] => {
    if (type === "contact") {
      return (
        contacts
          ?.filter((i) => i.contactId !== null)
          .map((item) => ({
            label: item.customerName,
            value: item?.contactId || "",
            // api/customers/search sends customerId (no "id"): the profile route needs it.
            secondValue: item?.customerId || item?.id || "",
            avatar: getInitialsFromFullName(item.customerName),
          })) || []
      );
    }
    if (type === "organization") {
      return (
        contacts
          ?.filter((i) => i.businessContactId !== null)
          .map((item) => ({
            label: `${item?.customerName}`,
            value: item.businessContactId || item.id || "",
            avatar: getInitialsFromFullName(item?.customerName || ""),
            avatarColor: "purple",
          })) || []
      );
    }
    return [];
  };

  return (
    <CustomSelect
      placeholder={placeholder}
      value={null}
      inputValue={value}
      options={getOptions()}
      onChangeInput={(newValue) => {
        setValue(newValue);
      }}
      removeSelectIcon={removeSelectIcon}
      isLoading={isLoadingContacts}
      clearValueCloseMenu={false}
      suggestionsOnly
      onChange={(newValue) => {
        close?.();
        const contact = newValue as SelectOptionType;
        const link =
          contact?.avatarColor === "purple"
            ? `/organization/${contact?.value}`
            : navigateToContact({
                type: "contact",
                customerId: contact.secondValue || "",
              });
        navigate(link);
      }}
      selectStyle={{
        ...(small ? { small: true } : { size: "md" }),
        fullWidth: true,
      }}
    />
  );
};

export default SearchContacts;
